import { Button } from "@/components/ui/button";
import { ContentLoader } from "@/components/ui/loaders";
import { OrderCard } from "@/components/fornada/campaign";
import { SegmentedControl } from "@/components/fornada/controls";
import { ConfirmSheet } from "@/components/fornada/sheet";
import {
  EmptyState,
  ScreenTitle,
  StatCell,
  StatStrip,
} from "@/components/fornada/primitives";
import { useActiveSale } from "@/hooks/use-active-sale";
import { useRemoveOrder } from "@/hooks/use-remove-order";
import { date } from "@/lib/date";
import { orderCode, pad2 } from "@/lib/format";
import { closeDateOf, isOrdersOpen, orderPositions, pizzasOf } from "@/lib/sale";
import { useAuth } from "@/providers/auth.provider";
import { Plus } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { sortBy } from "lodash";

const OrdersPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";
  const { data: sale, isLoading } = useActiveSale();
  const { mutate: removeOrder, isPending } = useRemoveOrder();
  const [removing, setRemoving] = useState<string | null>(null);
  const [scope, setScope] = useState<"mine" | "all">("mine");

  if (isLoading) return <ContentLoader show />;

  if (!sale) {
    return (
      <div className="flex flex-col gap-7">
        <ScreenTitle title="Meus pedidos" subtitle="Nenhuma campanha ativa no momento." />
        <EmptyState
          title="Nenhuma campanha ativa"
          description="Quando uma campanha for aberta, os pedidos aparecem aqui."
        />
      </div>
    );
  }

  const open = isOrdersOpen(sale);
  // Depois do prazo, só o ADMIN cria, edita ou remove pedidos.
  const canEdit = open || isAdmin;
  const showAll = isAdmin && scope === "all";
  const positions = orderPositions(sale.orders);
  const mine = sortBy(
    sale.orders.filter((o) => showAll || o.userId === user?.id),
    (o) => -date(o.createdAt as unknown as string).valueOf(),
  );
  const flavors = new Set(mine.flatMap((o) => o.items.map((i) => i.flavorId))).size;
  const removingOrder = mine.find((o) => o.id === removing);

  return (
    <div className="flex flex-col gap-6">
      <ScreenTitle
        eyebrow={sale.name}
        title={showAll ? "Pedidos" : "Meus pedidos"}
        subtitle={
          open
            ? sale.ordersManual
              ? "Dá para editar e remover pedidos enquanto as vendas estiverem abertas."
              : `Dá para editar e remover pedidos até ${closeDateOf(sale.endDate).format("DD/MM")}, 12h.`
            : isAdmin
              ? "Prazo encerrado: só administradores alteram os pedidos."
              : "Os pedidos desta campanha estão fechados."
        }
      >
        {canEdit && (
          <Button asChild className="hidden min-[960px]:inline-flex">
            <Link to="/orders/new">
              <Plus strokeWidth={2.2} /> Novo pedido
            </Link>
          </Button>
        )}
      </ScreenTitle>

      {isAdmin && (
        <SegmentedControl
          options={[
            { value: "mine", label: "Meus pedidos" },
            { value: "all", label: "Todos" },
          ]}
          value={scope}
          onChange={setScope}
          className="min-[960px]:max-w-sm"
        />
      )}

      <StatStrip>
        <StatCell label="Pizzas" value={pad2(pizzasOf(mine))} cream />
        <StatCell label="Pedidos" value={pad2(mine.length)} />
        <StatCell label="Sabores" value={pad2(flavors)} />
      </StatStrip>

      {mine.length > 0 ? (
        <div className="grid gap-2.5 min-[960px]:grid-cols-2 min-[960px]:gap-3">
          {mine.map((order) => (
            <OrderCard
              key={order.id}
              order={order}
              position={positions.get(order.id) ?? 0}
              seller={showAll ? order.user?.name : undefined}
              canEdit={canEdit}
              onEdit={() => navigate(`/orders/${order.id}/edit`)}
              onRemove={() => setRemoving(order.id)}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          title="Nenhum pedido ainda"
          description={
            canEdit
              ? "Registre o primeiro pedido da campanha."
              : "Você não registrou pedidos nesta campanha."
          }
        >
          {canEdit && (
            <Button asChild size="lg" className="mt-2">
              <Link to="/orders/new">
                <Plus /> Novo pedido
              </Link>
            </Button>
          )}
        </EmptyState>
      )}

      <ConfirmSheet
        open={!!removingOrder}
        onOpenChange={(o) => !o && setRemoving(null)}
        title={`Remover pedido ${orderCode(positions.get(removing ?? "") ?? 0)}?`}
        description="O pedido sai da campanha e das listas de compras e de pedidos. Essa ação não pode ser desfeita."
        confirmLabel="Remover pedido"
        destructive
        pending={isPending}
        onConfirm={() =>
          removing &&
          removeOrder(removing, { onSettled: () => setRemoving(null) })
        }
      />
    </div>
  );
};

export default OrdersPage;
